/**
 * Pitch — campo da gioco SVG con slot drag-target.
 *
 * Render:
 * - SVG background verde con linee del campo
 * - Per ogni slot del modulo, un cerchio drop-target posizionato in %
 * - Slot occupato mostra nome giocatore + badge ruolo
 * - Slot capitano/vice: badge "C" / "V" overlay
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { PlayerAvatar } from "../common/PlayerAvatar";
import { slotCompatibilityMantra } from "../common/ruoliMantraCompat";
import { resolveSlotRole, ruoloColorFromSlotLabel } from "../common/ruoliColors";
import type { Modulo, SlotPos } from "./pitchLayouts";
import { MODULO_LAYOUT, layoutMobile } from "./pitchLayouts";
import { etichetta } from "../common/etichette";
import { badgeProbabilita, testoInfoProbabilita } from "../common/probabilita";
import { FasciaBraccio } from "../common/FasciaBraccio";
import { etichetteSonoPro, siglaSlot, sigleRuoli } from "../common/nomenclatura";
import { tr } from "../../i18n";

export interface SlotState {
  giocatore_id: string | null;
  nome: string | null;
  ruoli_mantra: string[] | null;
  ruolo_classic: string | null;
}

/** Voto live/finale + bonus malus per un giocatore (subset di VotoGiocatoreItem). */
export interface VotoLive {
  voto_base: string | null;
  voto_finale: string | null;
  gol_fatti: number;
  gol_subiti: number;
  autogol: number;
  ammonizioni: number;
  espulsioni: number;
  assist: number;
  porta_inviolata: boolean;
  rigore_segnato: number;
  rigore_sbagliato: number;
  rigore_parato: number;
}

/** True se c'e' almeno un bonus/malus da mostrare accanto al voto. */
export function haBonusMalus(v: EventiBonus): boolean {
  return (
    v.gol_fatti > 0 ||
    v.assist > 0 ||
    v.ammonizioni > 0 ||
    v.espulsioni > 0 ||
    v.autogol > 0 ||
    v.rigore_segnato > 0 ||
    v.rigore_sbagliato > 0 ||
    v.rigore_parato > 0 ||
    v.porta_inviolata ||
    v.gol_subiti > 0
  );
}

/** Eventi che danno bonus/malus: il sottoinsieme di VotoLive che serve per
 * disegnarli (lo condividono voti live e dettaglio del calcolo). */
export type EventiBonus = Omit<VotoLive, "voto_base" | "voto_finale">;

/** Andrea 2026-09-17: l'assist e' una A bianca su fondo rosso disegnata da
 * noi. L'emoji 🅰 cambiava da un sistema all'altro (rossa sul telefono, blu
 * su Windows). Misure in em: segue la grandezza del testo attorno. */
export function IconaAssist() {
  return (
    <span
      aria-label={tr("Assist")}
      // Andrea 2026-10-29: grande come le altre icone (1.3em), non piu' la
      // "piccolina" accanto alle emoji
      className="inline-flex items-center justify-center w-[1.3em] h-[1.3em] rounded-[0.25em] bg-[#e11d48] text-white font-sans font-black text-[0.95em] leading-none align-middle"
    >
      A
    </span>
  );
}

interface VoceBonus {
  key: string;
  icona: ReactNode;
  /** Quante volte: accanto all'icona si scrive solo da 2 in su. */
  quanti: number;
  title: string;
  cls: string;
}

/** Bonus/malus nell'ordine di lettura: prima gol e assist, poi cartellini
 * e il resto. Andrea 2026-09-24: rigore parato = 🧱 (il muro), prima era
 * "🧤+" e si confondeva con la porta inviolata. */
function vociBonusMalus(v: EventiBonus): VoceBonus[] {
  const voci: VoceBonus[] = [
    {
      key: "gol",
      icona: "⚽",
      quanti: v.gol_fatti,
      title: tr("{{v1}} gol", { v1: v.gol_fatti }),
      cls: "text-success",
    },
    {
      key: "assist",
      icona: <IconaAssist />,
      quanti: v.assist,
      title: tr("{{v1}} assist", { v1: v.assist }),
      cls: "text-info",
    },
    {
      key: "amm",
      icona: "🟨",
      quanti: v.ammonizioni > 0 ? 1 : 0,
      title: tr("Ammonito"),
      cls: "text-warning",
    },
    {
      key: "esp",
      icona: "🟥",
      quanti: v.espulsioni > 0 ? 1 : 0,
      title: tr("Espulso"),
      cls: "text-danger",
    },
    {
      key: "autogol",
      icona: "🥅",
      quanti: v.autogol,
      title: tr("{{v1}} autogol", { v1: v.autogol }),
      cls: "text-danger",
    },
    {
      key: "rig-ok",
      icona: "🎯",
      quanti: v.rigore_segnato,
      title: tr("{{v1}} rigore segnato", { v1: v.rigore_segnato }),
      cls: "text-success",
    },
    {
      key: "rig-ko",
      icona: "❌",
      quanti: v.rigore_sbagliato,
      title: tr("{{v1}} rigore sbagliato", { v1: v.rigore_sbagliato }),
      cls: "text-danger",
    },
    {
      key: "rig-parato",
      icona: "🧱",
      quanti: v.rigore_parato,
      title: tr("{{v1}} rigore parato", { v1: v.rigore_parato }),
      cls: "text-info",
    },
    {
      key: "inviolata",
      icona: "🧤",
      quanti: v.porta_inviolata ? 1 : 0,
      title: tr("Porta inviolata"),
      cls: "text-success",
    },
    {
      key: "subiti",
      icona: "🔴",
      quanti: v.gol_subiti,
      title: tr("{{v1}} gol subiti", { v1: v.gol_subiti }),
      cls: "text-danger",
    },
  ];
  return voci.filter((x) => x.quanti > 0);
}

/** Icone di bonus/malus. In riga (default) accanto al voto: desktop sotto il
 * nome, panchina, recap. `verticale` = una per riga, per la colonna a destra
 * del cerchio su mobile (Andrea 2026-09-17). Piu' bonus uguali = una sola
 * icona col numero di fianco (tre gol: ⚽3). */
export function BonusMalus({ v, verticale = false }: { v: EventiBonus; verticale?: boolean }) {
  if (verticale) {
    // Andrea 2026-10-29: sul campo mobile ogni icona sta in una casella
    // della stessa misura (le emoji hanno larghezze diverse) e ha la sua
    // ombra, al posto del riquadro scuro che le conteneva tutte.
    return (
      <>
        {vociBonusMalus(v).map((x) => (
          <span key={x.key} title={x.title} className={`${x.cls} relative flex items-center`}>
            <span className="inline-flex items-center justify-center w-[1.3em] h-[1.3em] leading-none [filter:drop-shadow(0_1px_1.5px_rgba(0,0,0,0.95))]">
              {x.icona}
            </span>
            {/* il numero sporge a destra: le icone restano incolonnate */}
            {x.quanti > 1 && (
              <span className="absolute left-full top-1/2 -translate-y-1/2 ml-px font-mono font-bold tabular-nums text-[0.9em] text-white [text-shadow:0_1px_2px_rgba(0,0,0,0.95)]">
                {x.quanti}
              </span>
            )}
          </span>
        ))}
      </>
    );
  }
  return (
    <>
      {vociBonusMalus(v).map((x) => (
        <span key={x.key} title={x.title} className={`${x.cls} inline-flex items-center`}>
          {x.icona}
          {x.quanti > 1 && <span className="font-mono font-bold tabular-nums">{x.quanti}</span>}
        </span>
      ))}
    </>
  );
}

interface PitchProps {
  modulo: Modulo;
  slots: SlotState[]; // length = 11
  capitanoId: string;
  viceId: string;
  /** Override del label di ogni slot (es. dalla combinazione mantra o classic) */
  slotLabels?: string[];
  /** Se fornito, mostra voto live + badge bonus accanto a ogni slot (read-only). */
  votiPerGiocatore?: Map<string, VotoLive>;
  /** Giocatori da renderizzare semi-opachi (SV non sostituiti / sostituiti). */
  dimmedIds?: Set<string>;
  /** Titolari originali che sono stati sostituiti (badge "OUT" + opacity). */
  outIds?: Set<string>;
  /** Andrea 2026-09-12: chi ha giocato la partita reale ma non ha preso
   * voto (badge "SV"; se sostituito prevale "OUT"). */
  svIds?: Set<string>;
  /** Andrea 2026-09-14: chi deve ancora giocare. Mostra un 6 d'ufficio
   * provvisorio su sfondo bianco, per distinguerlo dal voto vero. */
  daGiocareIds?: Set<string>;
  /** Probabilità di giocare per giocatore (Andrea 2026-05-19): badge
   * colore accanto al nome per dare colpo d'occhio. */
  probabilitaPerGiocatore?: Map<
    string,
    { probabilita: number; fonte: string; note: string | null }
  >;
  /** Giocatori schierati ma NON in lista 25 ufficiale (Andrea 2026-05-21):
   * evidenziati con bordo rosso lampeggiante + tooltip. Bloccano la consegna. */
  fuoriLista25Ids?: Set<string>;
  onDropToSlot: (slotIdx: number, giocatoreId: string, sorgente: string) => void;
  onClickPlayer: (slotIdx: number) => void;
}

// Compatibilità slot ↔ giocatore: matrice Fantagazzetta condivisa
// (slotCompatibilityMantra restituisce "exact" | "malus" | "incompatible").

const TITLE_OUT = tr("Senza voto: sostituito da un panchinaro");
const TITLE_SV = tr("Ha giocato ma senza voto (SV), nessun sostituto disponibile");

function classicLetter(r: string | null): string {
  if (!r) return "?";
  const c = r.charAt(0).toUpperCase();
  return ["P", "D", "C", "A"].includes(c) ? c : "?";
}

/** Nome breve da mostrare nel cerchio del pitch.
 *
 * Andrea 2026-05-20: prima il codice faceva `nome.split(" ").slice(-1)`
 * (ultima parola) e troncava a 8 caratteri. Con i nomi tipici di
 * Fantacalcio.it ("Gudmundsson A.", "Ederson D.S.") l'ultima parola
 * è solo l'iniziale puntata → si vedeva "A." o "D.S." invece del cognome.
 *
 * Nuova logica:
 * - Se il nome contiene una "X." finale (iniziale del nome), prendi
 *   tutto ciò che la precede ("Gudmundsson A." → "Gudmundsson").
 * - Altrimenti tieni il nome così com'è ("De Gea", "Carnesecchi").
 * - Tronca con … a 11 char per stare nel cerchio.
 */
function displayNomeCorto(nome: string | null | undefined): string {
  if (!nome) return "";
  const s = nome.trim();
  // Match "Cognome [parole] X." o "Cognome X.S." (iniziali multiple)
  const m = s.match(/^(.+?)\s+[A-ZÀ-Ý](?:\.[A-ZÀ-Ý]?)*\.?\s*$/);
  const base = (m ? m[1] : s).trim();
  // Christian 2026-10-30: niente taglio a 11 lettere («De Ketelae…» con
  // spazio avanzato nello slot): se non ci sta, lo accorcia il CSS.
  return base;
}

export function Pitch({
  modulo,
  slots,
  capitanoId,
  viceId,
  slotLabels,
  votiPerGiocatore,
  dimmedIds,
  outIds,
  svIds,
  daGiocareIds,
  probabilitaPerGiocatore,
  fuoriLista25Ids,
  onDropToSlot,
  onClickPlayer,
}: PitchProps) {
  // Andrea 2026-09-08: sotto i 640px le linee troppo ravvicinate
  // (tipicamente il trequartista sopra il centrocampo) vengono
  // distanziate — le etichette sotto i cerchi occupano più spazio
  // relativo su schermo piccolo.
  // Andrea 2026-10-23: sul telefono il `title` non si vede: un tocco sul
  // badge (%, 🟥, ✚) apre un fumetto con la parola e il motivo, che si
  // chiude da solo dopo qualche secondo o al tocco successivo.
  const [infoSlot, setInfoSlot] = useState<string | null>(null);
  useEffect(() => {
    if (!infoSlot) return;
    const id = setTimeout(() => setInfoSlot(null), 4000);
    return () => clearTimeout(id);
  }, [infoSlot]);
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== "undefined" && window.innerWidth < 640,
  );
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 639px)");
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener("change", onChange);
    return () => mq.removeEventListener("change", onChange);
  }, []);
  const layoutBase: SlotPos[] = MODULO_LAYOUT[modulo] ?? MODULO_LAYOUT["4-3-3"];
  const layout: SlotPos[] = useMemo(
    () => (isMobile ? layoutMobile(layoutBase) : layoutBase),
    [isMobile, layoutBase],
  );
  const containerRef = useRef<HTMLDivElement>(null);
  // Nomenclatura Pro (29/09): le sigle si traducono solo se il campo e'
  // della modalità Pro (in Classic gli slot sono P/D/C/A).
  const slotPro = etichetteSonoPro(layout.map((pos, i) => slotLabels?.[i] ?? pos.label));

  return (
    <div
      ref={containerRef}
      className="relative w-full bg-gradient-to-b from-success/30 to-success/10 border-2 border-success/50 rounded-card overflow-hidden select-none"
      style={{ paddingTop: "133%" /* aspect 3:4 verticale */ }}
    >
      {/* Linee campo (SVG overlay) */}
      <svg
        className="absolute inset-0 w-full h-full pointer-events-none"
        viewBox="0 0 100 133"
        preserveAspectRatio="none"
      >
        {/* Bordi */}
        <rect
          x="2"
          y="2"
          width="96"
          height="129"
          fill="none"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="0.5"
        />
        {/* Linea metà campo */}
        <line x1="2" y1="66.5" x2="98" y2="66.5" stroke="rgba(255,255,255,0.4)" strokeWidth="0.5" />
        {/* Cerchio centrale */}
        <circle
          cx="50"
          cy="66.5"
          r="9"
          fill="none"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="0.5"
        />
        {/* Area di rigore in basso (tua) */}
        <rect
          x="25"
          y="111"
          width="50"
          height="20"
          fill="none"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="0.5"
        />
        <rect
          x="38"
          y="123"
          width="24"
          height="8"
          fill="none"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="0.5"
        />
        {/* Area di rigore in alto (avversario) */}
        <rect
          x="25"
          y="2"
          width="50"
          height="20"
          fill="none"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="0.5"
        />
        <rect
          x="38"
          y="2"
          width="24"
          height="8"
          fill="none"
          stroke="rgba(255,255,255,0.4)"
          strokeWidth="0.5"
        />
      </svg>

      {/* Slot */}
      {layout.map((pos, idx) => {
        const slot = slots[idx];
        const occupied = !!slot?.giocatore_id;
        const r = occupied ? classicLetter(slot?.ruolo_classic ?? null) : null;
        const isCap = occupied && slot?.giocatore_id === capitanoId;
        const isVice = occupied && slot?.giocatore_id === viceId;
        const label = slotLabels?.[idx] ?? pos.label;
        // Compatibilità Mantra: matrice Fantagazzetta condivisa. In Classic
        // saltiamo (i ruoli classic li gestisce il backend per macro).
        const compat = occupied
          ? slotCompatibilityMantra(slot?.ruoli_mantra ?? [], label, modulo)
          : "exact";
        const isMantraMismatch = occupied && compat === "incompatible";
        const isMantraMalus = occupied && compat === "malus";
        const isDimmed =
          occupied && slot?.giocatore_id != null && dimmedIds?.has(slot.giocatore_id) === true;
        const isOut =
          occupied && slot?.giocatore_id != null && outIds?.has(slot.giocatore_id) === true;
        const isSv =
          occupied && slot?.giocatore_id != null && svIds?.has(slot.giocatore_id) === true;
        const isDaGiocare =
          occupied && slot?.giocatore_id != null && daGiocareIds?.has(slot.giocatore_id) === true;
        // Voto live del giocatore, se la Pitch e' in modalita' partita.
        const votoLive =
          occupied && slot?.giocatore_id && votiPerGiocatore
            ? (votiPerGiocatore.get(slot.giocatore_id) ?? null)
            : null;
        const votoNumero =
          votoLive && (votoLive.voto_finale ?? votoLive.voto_base) != null
            ? Number(votoLive.voto_finale ?? votoLive.voto_base).toFixed(1)
            : null;
        // Probabilità di giocare (Andrea 2026-05-19, spostata accanto al
        // nome il 2026-09-08). Soglie e colori in common/probabilita.ts:
        // `cls` = versione trasparente, `solid` = sfondo pieno per
        // l'overlay sul cerchio, leggibile sopra l'avatar.
        const badgeProb = (() => {
          if (!occupied || !slot?.giocatore_id || !probabilitaPerGiocatore) return null;
          const pr = probabilitaPerGiocatore.get(slot.giocatore_id);
          if (!pr) return null;
          return {
            ...badgeProbabilita(pr.probabilita, pr.note),
            tooltip: testoInfoProbabilita(pr.probabilita, pr.note, etichetta(pr.fonte)),
          };
        })();
        // Andrea 2026-05-21: giocatore non in lista 25 ufficiale
        const isFuoriLista25 =
          occupied &&
          slot?.giocatore_id != null &&
          fuoriLista25Ids?.has(slot.giocatore_id) === true;

        return (
          <div
            key={idx}
            onDragOver={(e) => {
              e.preventDefault();
              e.currentTarget.classList.add("ring-2", "ring-accent");
            }}
            onDragLeave={(e) => {
              e.currentTarget.classList.remove("ring-2", "ring-accent");
            }}
            onDrop={(e) => {
              e.preventDefault();
              e.currentTarget.classList.remove("ring-2", "ring-accent");
              const data = e.dataTransfer.getData("application/x-fantasquad");
              if (!data) return;
              const { giocatore_id, sorgente } = JSON.parse(data);
              onDropToSlot(idx, giocatore_id, sorgente);
            }}
            onClick={() => onClickPlayer(idx)}
            // Andrea 2026-09-08: su mobile lo slot è più stretto (17%)
            // perché con 4-5 giocatori in linea i cerchi si toccavano;
            // da sm in su resta al 20% di prima.
            className={`absolute transition-opacity w-[17%] sm:w-[20%] ${isDimmed ? "opacity-40" : "opacity-100"} ${infoSlot !== null && infoSlot === slot?.giocatore_id ? "z-50" : ""}`}
            style={{
              left: `${pos.x}%`,
              // pos.y in 0-100 (0=alto, 100=basso) = % dell'altezza container.
              // Il container ha paddingTop:133% (aspect 3:4) ma `top: X%` e' gia'
              // calcolato sull'altezza totale del padding-box, quindi pos.y%
              // basta. Il bug precedente (* 133/100) spingeva il portiere
              // (y=92) a 122% → fuori dall'area visibile.
              top: `${pos.y}%`,
              transform: "translate(-50%, -50%)",
              maxWidth: "80px",
            }}
            draggable={occupied}
            onDragStart={(e) => {
              if (!occupied || !slot?.giocatore_id) return;
              e.dataTransfer.setData(
                "application/x-fantasquad",
                JSON.stringify({
                  giocatore_id: slot.giocatore_id,
                  sorgente: `slot:${idx}`,
                }),
              );
              e.dataTransfer.effectAllowed = "move";
            }}
          >
            <div
              className={`relative aspect-square rounded-full flex items-center justify-center border-[3px] transition cursor-pointer overflow-visible ${
                occupied
                  ? isFuoriLista25
                    ? "bg-danger/30 border-danger shadow-lg ring-4 ring-danger/60 animate-pulse"
                    : isMantraMismatch
                      ? // Andrea 2026-05-23: ring spesso + bordo più
                        // grosso per ROSSO incompatibile (super visibile)
                        "bg-bg-card border-danger shadow-lg ring-4 ring-danger/50"
                      : isMantraMalus
                        ? // Bordo + ring GIALLO per malus -1 (prima si
                          // confondeva con l'accent oro del bordo normale)
                          "bg-bg-card border-warning shadow-lg ring-4 ring-warning/50"
                        : "bg-bg-card border-success shadow-lg hover:ring-2 hover:ring-accent"
                  : "bg-primary-deep/50 border-fg-muted/30 border-dashed hover:bg-primary-deep/70"
              }`}
              title={
                isFuoriLista25
                  ? tr(
                      "⚠ {{v1}} NON è in lista 25 ufficiale: sostituiscilo o sarà sostituito automaticamente alla consegna",
                      { v1: slot?.nome ?? "?" },
                    )
                  : isMantraMismatch
                    ? tr("Non schierabile: slot {{v1}}, il giocatore ha {{v2}}", {
                        v1: siglaSlot(label, slotPro),
                        v2: slot?.ruoli_mantra ? sigleRuoli(slot.ruoli_mantra) : "?",
                      })
                    : isMantraMalus
                      ? tr("Fuori ruolo (malus -1): slot {{v1}}, giocatore {{v2}}", {
                          v1: siglaSlot(label, slotPro),
                          v2: slot?.ruoli_mantra ? sigleRuoli(slot.ruoli_mantra) : "?",
                        })
                      : occupied
                        ? (slot?.nome ?? "")
                        : siglaSlot(label, slotPro)
              }
            >
              {/* Pallino con il ruolo MANTRA. Andrea 2026-05-23: per
                  slot polivalenti "X/Y" mostra SOLO il ruolo che il
                  giocatore effettivamente ricopre (resolveSlotRole). */}
              {occupied &&
                (() => {
                  const ruoloAttivo = resolveSlotRole(label, slot?.ruoli_mantra ?? null);
                  return (
                    <span
                      className={`absolute -top-1 -left-1 z-10 text-[9px] font-bold min-w-[20px] h-5 px-1 rounded-md flex items-center justify-center font-mono leading-none ${ruoloColorFromSlotLabel(ruoloAttivo)}`}
                    >
                      {(ruoloAttivo ? siglaSlot(ruoloAttivo, slotPro) : "") || r || "?"}
                    </span>
                  );
                })()}
              {/* Andrea 2026-09-08: su MOBILE il malus/incompatibilità va
                  in alto a destra, opposto al pallino del ruolo (sotto il
                  nome resterebbe una riga in più, e le linee si toccano). */}
              {(isMantraMismatch || isMantraMalus) && (
                <span
                  className={`absolute -top-1 -right-1.5 z-20 text-[8px] leading-none font-bold font-mono px-1 py-0.5 rounded shadow ${
                    isMantraMismatch
                      ? "bg-danger/80 text-white border border-danger/80"
                      : "bg-warning/80 text-ink border border-warning/80"
                  }`}
                  title={
                    isMantraMismatch
                      ? tr("Non schierabile in questo slot ({{v1}})", {
                          v1: siglaSlot(label, slotPro),
                        })
                      : tr("Fuori ruolo (slot {{v1}}): malus -1 al voto", {
                          v1: siglaSlot(label, slotPro),
                        })
                  }
                >
                  {/* Andrea 2026-09-08: solo il valore, senza la lettera
                      del ruolo — quella è già nel pallino in alto a
                      sinistra e allungava troppo la pastiglia. */}
                  {isMantraMismatch ? "✗" : "-1"}
                </span>
              )}
              {/* Andrea 2026-09-08: su MOBILE la percentuale va in basso
                  a destra del cerchio — opposta al pallino del ruolo — su
                  sfondo pieno, così resta leggibile sopra l'avatar e non
                  ruba spazio al nome. Su desktop resta sotto il nome. */}
              {/* Andrea 2026-10-23: squalificati e infortunati → solo il
                  simbolo (🟥 / ✚) in basso a destra al posto della %, la
                  parola nel tooltip. */}
              {badgeProb && !votoLive && (
                <span
                  className={`absolute -bottom-1 -right-1.5 z-20 leading-none font-bold font-mono px-1 py-0.5 rounded shadow whitespace-nowrap ${badgeProb.icona ? "text-[10px]" : "text-[8px]"} ${badgeProb.solid}`}
                  title={badgeProb.tooltip}
                  onClick={
                    // Andrea 2026-10-23: il fumetto solo per 🟥 / ✚, la % no
                    badgeProb.icona
                      ? (e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          const k = slot?.giocatore_id ?? null;
                          setInfoSlot((cur) => (cur === k ? null : k));
                        }
                      : undefined
                  }
                >
                  {badgeProb.icona ?? badgeProb.label}
                </span>
              )}
              {badgeProb && !votoLive && infoSlot !== null && infoSlot === slot?.giocatore_id && (
                <div
                  className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1.5 z-40 w-44 rounded-lg bg-primary-deep border border-accent/50 shadow-xl px-2 py-1.5 text-[10px] leading-snug text-fg text-center whitespace-normal"
                  onClick={(e) => {
                    e.stopPropagation();
                    setInfoSlot(null);
                  }}
                >
                  {badgeProb.tooltip}
                </div>
              )}
              {/* Andrea 2026-09-12: su MOBILE il voto sta in basso a destra del
                  cerchio, come la percentuale in Schiera. Andrea 2026-09-17:
                  nella pastiglia resta SOLO il voto — coi bonus dentro si
                  allargava fino a coprire la fascia di capitano/vice. I
                  bonus/malus vanno in colonna sul fianco destro del cerchio,
                  sopra il voto. */}
              {votoNumero !== null && (
                <span
                  className="inline-flex sm:hidden absolute -bottom-1 -right-1.5 z-20 items-center whitespace-nowrap text-[8px] leading-none font-mono font-bold tabular-nums text-accent px-0.5 py-0.5 rounded shadow bg-primary-deep/90 border border-accent/50"
                  title={tr("Voto {{v1}}", { v1: votoNumero })}
                >
                  {votoNumero}
                </span>
              )}
              {/* Andrea 2026-09-17: SV e OUT al posto del voto (prima in
                  basso a sinistra: sfalsavano le icone e su capitano/vice
                  coprivano la fascia). Chi e' SV/OUT un voto non ce l'ha,
                  quindi il posto e' libero. */}
              {votoNumero === null && (isOut || isSv) && (
                <span
                  className={`inline-flex sm:hidden absolute -bottom-1 -right-1.5 z-20 items-center whitespace-nowrap text-[8px] leading-none font-mono font-bold px-0.5 py-0.5 rounded shadow ${
                    isOut ? "bg-danger text-fg" : "bg-fg-muted text-ink"
                  }`}
                  title={isOut ? TITLE_OUT : TITLE_SV}
                >
                  {isOut ? "OUT" : "SV"}
                </span>
              )}
              {votoLive && haBonusMalus(votoLive) && (
                <span className="flex sm:hidden absolute bottom-[11px] -right-2 z-20 flex-col items-start gap-px text-[10px] leading-none">
                  <BonusMalus v={votoLive} verticale />
                </span>
              )}
              {isDaGiocare && !votoLive && (
                <span
                  className="inline-flex sm:hidden absolute -bottom-1 -right-1.5 z-20 items-center whitespace-nowrap text-[8px] leading-none font-mono font-bold tabular-nums px-0.5 py-0.5 rounded shadow bg-white text-[#2A0F4E] border border-primary/40"
                  title={tr("Deve ancora giocare: 6 d'ufficio provvisorio")}
                >
                  6.0
                </span>
              )}

              {occupied ? (
                // Andrea 2026-05-21: avatar foto (se disponibile in
                // /public/avatars/) altrimenti iniziali su gradient.
                // Il bordo colorato resta sul wrapper esterno.
                <div className="absolute inset-1 rounded-full overflow-hidden">
                  <PlayerAvatar nome={slot?.nome} size={56} className="!w-full !h-full !border-0" />
                </div>
              ) : (
                <span className="text-[9px] font-mono text-fg-muted text-center px-0.5 leading-tight">
                  {siglaSlot(label, slotPro) || "?"}
                </span>
              )}

              {/* Andrea 2026-09-02: fascia capitano (grafica riferimento:
                  bande + cerchio con lettera) come targhetta centrata sul
                  bordo basso del cerchio — sul braccio dell'icona non ci
                  stava. Oro/C per il Cap, argento/VC per il Vice. */}
              {isCap && (
                <span
                  className="absolute -bottom-1.5 left-[30%] -translate-x-1/2 w-[44%] z-10 block"
                  title={tr("Capitano")}
                >
                  <FasciaBraccio />
                </span>
              )}
              {isVice && (
                <span
                  className="absolute -bottom-1.5 left-[30%] -translate-x-1/2 w-[44%] z-10 block"
                  title={tr("Vicecapitano")}
                >
                  <FasciaBraccio vice />
                </span>
              )}
            </div>
            {occupied && (
              // Andrea 2026-09-08: più respiro tra icona, nome e badge —
              // con la fascia da capitano sul bordo del cerchio stava
              // tutto appiccicato.
              <div className="text-center mt-2 leading-tight space-y-1">
                {/* Nome + percentuale sulla STESSA riga: su mobile, con
                    4-5 giocatori in linea, incolonnarli rubava altezza e
                    i blocchi si toccavano (Andrea 2026-09-08). */}
                {/* Andrea 2026-05-21: cognome sotto il cerchio cosi' il
                    giocatore e' identificabile anche con la foto avatar.
                    Andrea 2026-05-22: classic letter (P/D/C/A) come label
                    grigia secondaria — il ruolo mantra e' ora in pallino. */}
                <div className="text-[8px] sm:text-[9px] font-bold text-fg truncate px-0.5">
                  {displayNomeCorto(slot?.nome)}
                </div>
                {/* Andrea 2026-05-23: label classic letter sotto il
                    cerchio — quando fuori-ruolo/malus diventa un
                    mini-badge con sfondo colorato, molto più visibile
                    di un semplice testo colorato. */}
                {/* Andrea 2026-09-08: niente lettera classic sotto il
                    nome quando il giocatore è a posto — il ruolo Mantra
                    nel pallino basta, la doppia sigla confondeva. I
                    badge di malus/incompatibilità sopra restano. */}
              </div>
            )}

            {/* Voto live + bonus/malus sotto il nome — SOLO DESKTOP (su
                mobile stanno nel cerchio). Andrea 2026-09-14: bonus/malus
                sulla stessa riga del voto, se no andavano a capo e
                sfalsavano le icone; emoji piu' grandi, si leggono. */}
            {(votoLive || isOut || isSv) && (
              <div className="hidden sm:flex mt-0.5 items-center justify-center gap-1 whitespace-nowrap">
                {votoNumero !== null ? (
                  <span className="text-[10px] font-mono font-bold tabular-nums text-accent bg-primary-deep/90 rounded px-1">
                    {votoNumero}
                  </span>
                ) : (
                  // Andrea 2026-09-17: SV/OUT dove starebbe il voto.
                  (isOut || isSv) && (
                    <span
                      className={`text-[10px] font-mono font-bold rounded px-1 ${
                        isOut ? "bg-danger text-fg" : "bg-fg-muted text-ink"
                      }`}
                      title={isOut ? TITLE_OUT : TITLE_SV}
                    >
                      {isOut ? "OUT" : "SV"}
                    </span>
                  )
                )}
                {votoLive && (
                  <span className="flex items-center gap-0.5 text-[12px] leading-none">
                    <BonusMalus v={votoLive} />
                  </span>
                )}
              </div>
            )}
            {isDaGiocare && !votoLive && (
              <div className="hidden sm:flex mt-0.5 items-center justify-center">
                <span
                  className="text-[10px] font-mono font-bold tabular-nums bg-white text-[#2A0F4E] rounded px-1"
                  title={tr("Deve ancora giocare: 6 d'ufficio provvisorio")}
                >
                  6.0
                </span>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
